import { createContext, useContext } from 'react'
import { catalogSettings } from '@skop-app/lib-domain/constants'
import type {
  Authorization,
  OrganizationSettingsProps,
  Ownership,
  Subscription,
  Team,
  UserInput,
  UserTeam,
} from '@skop-app/lib-domain/types'
import type { OrganizationScope } from '@/utils/organization'
import type {
  ParsedTeamAuthorizationPolicy,
  TeamAuthorizationPolicyV1,
} from '@/features/permissions/policy'
import type { AuthorizationConstraints } from '@/features/permissions'
import type {
  ParsedTeamReviewWorkflowPolicy,
  TeamReviewWorkflowPolicyV1,
} from '@/features/reviews'

export type ManageUserContextValue = {
  user: UserInput | undefined
  organizationScope: OrganizationScope<Team>
  /**
   * @deprecated Use organizationScope.selectedOrganization instead.
   */
  team: Team | undefined
  /**
   * @deprecated Use organizationScope.selectedOrganization instead.
   */
  teamSummary: Team | undefined
  /**
   * @deprecated Use organization memberships explicitly and compare project/reference organization IDs against organizationScope.activeOrganizationId.
   * `UserTeam.value` is the organization ID; `UserTeam.id` is a membership ID.
   */
  teams: UserTeam[]
  /**
   * Raw organization memberships, including personal SOLO workspaces.
   * Keep this separate from selectable teams and selectedTeamId, which omit SOLO workspaces.
   */
  organizationMemberships: UserTeam[]
  /**
   * @deprecated Use organizationScope.selectedOrganizationId instead.
   */
  selectedTeamId: string | null
  isFetching: boolean
  isLoading: boolean
  isLoadingUser: boolean
  isLoadingTeam: boolean
  isLoadingSelectedOrganization: boolean
  isOrganizationHydrationError: boolean
  retryOrganizationHydration: () => Promise<{ isError: boolean }>
  isRotor: boolean
  isClient: boolean
  subscription: Subscription | undefined
  authorization: Authorization | undefined
  ownership: Ownership
  customSettings: OrganizationSettingsProps
  isGuest: boolean
  isAdmin: boolean
  isSkopAdmin: boolean
  teamAuthorizationPolicy: TeamAuthorizationPolicyV1 | null
  teamAuthorizationPolicySource: ParsedTeamAuthorizationPolicy['source']
  teamAuthorizationPolicyError: string | null
  teamReviewWorkflowPolicy: TeamReviewWorkflowPolicyV1 | null
  teamReviewWorkflowPolicySource: ParsedTeamReviewWorkflowPolicy['source']
  teamReviewWorkflowPolicyError: string | null
  authorizationConstraints: AuthorizationConstraints
}

export const UserContextValue = createContext<ManageUserContextValue>({
  user: undefined,
  organizationScope: {
    scopeKind: 'unresolved',
    selectedOrganizationId: null,
    activeOrganizationId: undefined,
    activeOrganizationMasterId: undefined,
    selectedOrganization: undefined,
    selectedOrganizationParent: undefined,
    isSolo: false,
  },
  team: undefined,
  teamSummary: undefined,
  teams: [],
  organizationMemberships: [],
  selectedTeamId: null,
  isFetching: false,
  isLoading: false,
  isLoadingUser: false,
  isLoadingTeam: false,
  isLoadingSelectedOrganization: false,
  isOrganizationHydrationError: false,
  retryOrganizationHydration: async () => ({ isError: false }),
  isRotor: false,
  isClient: false,
  subscription: undefined,
  authorization: undefined,
  ownership: { source: 'manage' } as Ownership,
  customSettings: catalogSettings,
  isGuest: false,
  isAdmin: false,
  isSkopAdmin: false,
  teamAuthorizationPolicy: null,
  teamAuthorizationPolicySource: 'none',
  teamAuthorizationPolicyError: null,
  teamReviewWorkflowPolicy: null,
  teamReviewWorkflowPolicySource: 'none',
  teamReviewWorkflowPolicyError: null,
  authorizationConstraints: {},
})

export function useUserContext() {
  return useContext(UserContextValue)
}
